import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CustomerCacheEntry } from '../../types/customer';

const getInitials = (name: string): string =>
  name
    .split(/[\s,]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const customerSubtitle = (customer: CustomerCacheEntry): string => {
  const poolCount =
    customer.totalPools === 1 ? '1 pool' : `${customer.totalPools} pools`;
  return poolCount;
};

interface PoolCustomerPickerProps {
  customers: CustomerCacheEntry[];
  loading: boolean;
  value: CustomerCacheEntry | null;
  onChange: (customer: CustomerCacheEntry | null) => void;
  error?: string;
  /** Set when the underlying customer cache subscription failed - distinct
   * from the genuine "no customers exist yet" empty state. */
  fetchError?: string | null;
}

/**
 * Search-select picker for linking a pool to an existing customer. No inline
 * customer creation - only pool count is shown as subtext since there's no
 * account-type field on CustomerCacheEntry.
 */
const PoolCustomerPicker = ({
  customers,
  loading,
  value,
  onChange,
  error,
  fetchError,
}: PoolCustomerPickerProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const filteredCustomers = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    if (!searchLower) return customers;
    return customers.filter((customer) =>
      customer.name.toLowerCase().includes(searchLower)
    );
  }, [customers, search]);

  const handleSelect = (customer: CustomerCacheEntry) => {
    onChange(customer);
    setOpen(false);
    setSearch('');
  };

  const disabled = Boolean(fetchError) && !value;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        className={`flex min-h-[50px] w-full items-center gap-2.5 rounded-xl border-[1.5px] bg-white px-3.5 py-2 text-left transition-colors ${
          disabled ? 'cursor-not-allowed opacity-60' : ''
        } ${
          error || fetchError
            ? 'border-danger'
            : open
              ? 'border-primary shadow-[0_0_0_3px_rgba(0,105,148,0.1)]'
              : 'border-grey hover:border-secondary'
        }`}
      >
        {fetchError && !value ? (
          <span className="flex flex-1 items-center gap-2 text-[15px] text-danger">
            <X className="h-4 w-4 flex-shrink-0" />
            Couldn't load customers
          </span>
        ) : value ? (
          <span className="flex min-w-0 flex-1 items-center gap-2.5">
            <span className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-[10px] bg-primary text-xs font-bold text-white">
              {getInitials(value.name)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-bold text-dark">
                {value.name}
              </span>
              <span className="block text-xs text-grey10">
                {customerSubtitle(value)}
              </span>
            </span>
          </span>
        ) : (
          <span className="flex-1 text-[15px] text-grey10">
            Search and select a customer…
          </span>
        )}
        {value && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onChange(null);
              }
            }}
            title="Clear"
            aria-label="Clear selected customer"
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-grey4 text-light hover:bg-grey3"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown className="h-[18px] w-[18px] flex-shrink-0 text-grey10" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 overflow-hidden rounded-2xl border border-grey3 bg-white shadow-[0_16px_40px_rgba(0,29,47,0.16)]">
          <div className="relative border-b border-grey3 p-3">
            <Search className="pointer-events-none absolute left-6 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-grey10" />
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search existing customers…"
              className="h-[42px] w-full rounded-[10px] border border-grey pl-[38px] pr-3 text-sm text-dark outline-none focus:border-primary"
            />
          </div>
          <div className="max-h-[232px] overflow-y-auto p-1.5">
            {loading ? (
              <div className="px-4 py-[22px] text-center text-[13px] text-light">
                Loading customers…
              </div>
            ) : fetchError ? (
              <div className="px-4 py-[22px] text-center text-[13px] text-danger">
                {fetchError}
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="px-4 py-[22px] text-center text-[13px] text-light">
                {customers.length === 0
                  ? 'No customers yet. Create one from the Customers tab first.'
                  : 'No customers match your search.'}
              </div>
            ) : (
              filteredCustomers.map((customer) => {
                const selected = value?.id === customer.id;
                return (
                  <div
                    key={customer.id}
                    role="option"
                    aria-selected={selected}
                    tabIndex={0}
                    onClick={() => handleSelect(customer)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSelect(customer);
                    }}
                    className={`flex cursor-pointer items-center gap-2.5 rounded-[10px] px-3 py-2.5 ${
                      selected ? 'bg-cloud' : 'hover:bg-cloud'
                    }`}
                  >
                    <span className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-[10px] bg-primary text-xs font-bold text-white">
                      {getInitials(customer.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-dark">
                        {customer.name}
                      </span>
                      <span className="block text-xs text-grey10">
                        {customerSubtitle(customer)}
                      </span>
                    </span>
                    {selected && (
                      <span className="ml-auto flex-shrink-0 text-primary">
                        <Check className="h-[18px] w-[18px]" />
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PoolCustomerPicker;
